import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Grid, Modal, Tab, Tabs, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useHistory } from 'react-router-dom';
import Title from '../../components/common/Title';
import Alert from '../../components/common/Alert';
import TableSite from '../../components/common/Table';
import Search from '../../components/common/Table/searchBox';
import Menu, { MenuContainer } from '../../components/common/Menu';
import Button from '../../components/common/Button';
import ModalContent from '../../components/common/Modal/ModalContent';
import StatusIndicator from '../../components/common/StatusIndicator';
import CatalogApi from './catalog-api';
import CatalogPageSettings from './CatalogPageSettings';
import {
  CATEGORY_OPTIONS,
  getCatalogOccasionLabel,
  getCatalogOccasionOptions,
} from './constants';
import { USER_STATUS_CLASSNAMES } from '../ClientContainer/constants';
import useDebounce from '../../hooks/useDebounce';

const STATUS_METADATA = {
  pendiente: {
    label: 'Pendiente',
    className: USER_STATUS_CLASSNAMES.deactivated,
  },
  aprobada: {
    label: 'Aprobada',
    className: USER_STATUS_CLASSNAMES.active,
  },
  rechazada: {
    label: 'Rechazada',
    className: USER_STATUS_CLASSNAMES.rejected,
  },
};

const STATUS_OPTIONS = [
  {
    status: 'aprobada',
    label: 'Cambiar a Aprobada',
  },
  {
    status: 'rechazada',
    label: 'Cambiar a Rechazada',
  },
  {
    status: 'pendiente',
    label: 'Cambiar a Pendiente',
  },
];

const CATALOG_TABS = {
  POSTULATIONS: 'postulations',
  SETTINGS: 'settings',
};

const renderCellText = (value) => (
  <span
    style={{
      color: '#7f7f7f',
      display: 'block',
      lineHeight: '30px',
    }}
  >
    {value || '-'}
  </span>
);

const CatalogContainer = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(CATALOG_TABS.POSTULATIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [postulations, setPostulations] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [featuredContextFilter, setFeaturedContextFilter] = useState('');
  const [featuredContextOptions, setFeaturedContextOptions] = useState([]);
  const [isLoadingFeaturedContextOptions, setIsLoadingFeaturedContextOptions] =
    useState(true);
  const [featuredContextOptionsError, setFeaturedContextOptionsError] =
    useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [regionOptions, setRegionOptions] = useState([]);
  const [message, setMessage] = useState('');
  const [typeMessage, setTypeMessage] = useState('info');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    postulationId: null,
    reason: '',
    error: '',
  });
  const debouncedSearchText = useDebounce(searchText.trim(), 400);

  const getStatusIndicator = (status) => {
    const metadata = STATUS_METADATA[status] || STATUS_METADATA.pendiente;

    return (
      <div style={{ opacity: 0.75 }}>
        <StatusIndicator
          statusClassName={metadata.className}
          text={metadata.label}
        />
      </div>
    );
  };

  const fetchPostulations = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};

      if (debouncedSearchText) {
        params.keyword = debouncedSearchText;
      }

      if (categoryFilter) {
        params.category = categoryFilter;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (featuredContextFilter) {
        params.featuredContext = featuredContextFilter;
      }

      if (regionFilter) {
        params.region = regionFilter;
      }

      const {
        data: { postulations: catalogPostulations = [] },
      } = await CatalogApi.fetchPostulations(params);

      setPostulations(
        catalogPostulations.map((postulation) => ({
          id: postulation.id,
          user: postulation.user,
          userName: postulation.userName,
          userEmail: postulation.userEmail,
          userCelphone: postulation.userCelphone,
          companyName: postulation.companyName,
          category: postulation.category,
          featuredContexts:
            postulation.featuredContexts &&
            postulation.featuredContexts.length > 0
              ? postulation.featuredContexts.join(', ')
              : '-',
          status: postulation.status,
          rejectionReason: postulation.rejectionReason || null,
          createdAt: moment(postulation.createdAt).format('DD/MM/YYYY HH:mm'),
        }))
      );
    } catch (error) {
      setMessage(
        error?.data?.message ||
          'Ocurrió un error al cargar las postulaciones del catálogo.'
      );
      setTypeMessage('error');
    } finally {
      setIsLoading(false);
    }
  }, [
    categoryFilter,
    debouncedSearchText,
    featuredContextFilter,
    regionFilter,
    statusFilter,
  ]);

  useEffect(() => {
    fetchPostulations();
  }, [fetchPostulations]);

  useEffect(() => {
    if (activeTab !== CATALOG_TABS.POSTULATIONS) {
      return undefined;
    }

    let isMounted = true;

    const loadFeaturedContextOptions = async () => {
      try {
        setIsLoadingFeaturedContextOptions(true);
        setFeaturedContextOptionsError('');
        const {
          data: { occasions = [] } = {},
        } = await CatalogApi.fetchOccasions();

        if (!isMounted) {
          return;
        }

        const nextOptions = getCatalogOccasionOptions(occasions);

        setFeaturedContextOptions(nextOptions);
        setFeaturedContextFilter((currentFilter) =>
          currentFilter &&
          !nextOptions.some((occasion) => occasion.name === currentFilter)
            ? ''
            : currentFilter
        );
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setFeaturedContextOptionsError(
          error?.data?.message ||
            'Ocurrió un error al cargar las ocasiones del catálogo.'
        );
      } finally {
        if (isMounted) {
          setIsLoadingFeaturedContextOptions(false);
        }
      }
    };

    loadFeaturedContextOptions();

    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  useEffect(() => {
    let isMounted = true;

    const loadRegions = async () => {
      try {
        const { data = [] } = await CatalogApi.fetchRegions();

        if (!isMounted) {
          return;
        }

        const nextRegionOptions = [...new Set([
          ...(Array.isArray(data)
            ? data
                .map((region) =>
                  typeof region?.name === 'string' ? region.name.trim() : ''
                )
                .filter(Boolean)
            : []),
          regionFilter,
        ].filter(Boolean))].sort((left, right) =>
          left.localeCompare(right, 'es-CL')
        );

        setRegionOptions(nextRegionOptions);
      } catch (error) {
        console.error('Error loading catalog regions', error);
      }
    };

    loadRegions();

    return () => {
      isMounted = false;
    };
  }, [regionFilter]);

  useEffect(() => {
    if (openMenuId === null) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (event.target.closest('.action-container')) {
        return;
      }

      setOpenMenuId(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openMenuId]);

  const onMenuClick = (id) => {
    setOpenMenuId((currentOpenMenuId) =>
      currentOpenMenuId === id ? null : id
    );
  };

  const goToDetail = useCallback(
    (userId) => {
      setOpenMenuId(null);
      history.push(`/catalogo/detalle/${userId}`);
    },
    [history]
  );

  const closeRejectionModal = () => {
    setRejectionModal({
      isOpen: false,
      postulationId: null,
      reason: '',
      error: '',
    });
  };

  const openRejectionModal = useCallback((id) => {
    setOpenMenuId(null);
    setRejectionModal({
      isOpen: true,
      postulationId: id,
      reason: '',
      error: '',
    });
  }, []);

  const updateStatus = useCallback(async (id, status, rejectionReason = null) => {
    try {
      setIsUpdatingStatus(true);
      await CatalogApi.updatePostulationStatus(id, status, rejectionReason);
      await fetchPostulations();

      setMessage('Estado de la postulación actualizado con éxito.');
      setTypeMessage('success');
      return true;
    } catch (error) {
      setMessage(
        error?.data?.message ||
          'Ocurrió un error al actualizar el estado de la postulación.'
      );
      setTypeMessage('error');
      return false;
    } finally {
      setIsUpdatingStatus(false);
      setOpenMenuId(null);
    }
  }, [fetchPostulations]);

  const handleSearchPostulations = useCallback((event) => {
    const { value = '' } = event?.target || {};
    setSearchText(value);
  }, []);

  const confirmRejectedStatus = async () => {
    const normalizedReason = rejectionModal.reason.trim();

    if (!normalizedReason || !rejectionModal.postulationId) {
      setRejectionModal((currentModal) => ({
        ...currentModal,
        error: 'Debes ingresar un motivo de rechazo.',
      }));
      return;
    }

    const wasUpdated = await updateStatus(
      rejectionModal.postulationId,
      'rechazada',
      normalizedReason
    );

    if (wasUpdated) {
      closeRejectionModal();
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Nombre',
        accessor: 'userName',
        Cell: ({ value }) => renderCellText(value),
      },
      {
        Header: 'Email',
        accessor: 'userEmail',
        Cell: ({ value }) => renderCellText(value),
      },
      {
        Header: 'Emprendimiento',
        accessor: 'companyName',
        Cell: ({ value }) => renderCellText(value),
      },
      {
        Header: 'Categoría',
        accessor: 'category',
        Cell: ({ value }) => renderCellText(value),
      },
      {
        Header: 'Contextos',
        accessor: 'featuredContexts',
        Cell: ({ value }) => renderCellText(value),
      },
      {
        Header: 'Estado',
        accessor: 'status',
        Cell: ({ value }) => getStatusIndicator(value),
      },
      {
        Header: 'Fecha',
        accessor: 'createdAt',
        Cell: ({ value }) => renderCellText(value),
      },
      {
        Header: 'Acción',
        id: 'actions',
        isCentered: true,
        Cell: ({ row: { original } }) => {
          const availableItems = [
            {
              text: 'Ver detalle',
              action: () => goToDetail(original.user),
            },
            ...STATUS_OPTIONS.filter(
              (option) => option.status !== original.status
            ).map((option) => ({
              text: option.label,
              action: () =>
                option.status === 'rechazada'
                  ? openRejectionModal(original.id)
                  : updateStatus(original.id, option.status),
            })),
          ];

          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <MenuContainer
                classActive={`${
                  openMenuId === original.id ? 'active-action' : ''
                }`}
                onClick={() => onMenuClick(original.id)}
              >
                <Menu
                  open={openMenuId === original.id}
                  items={availableItems}
                  id={original.id}
                />
              </MenuContainer>
            </div>
          );
        },
        disableSortBy: true,
      },
    ],
    [goToDetail, openMenuId, openRejectionModal, updateStatus]
  );

  const pageSubtitle =
    activeTab === CATALOG_TABS.POSTULATIONS
      ? 'Acá podrás revisar la lista de postulaciones enviadas al catálogo de la plataforma.'
      : 'Configura la creación y edición de ocasiones visibles en el catálogo público.';

  return (
    <div className="user-container catalog-container">
      {message && <Alert type={typeMessage}>{message}</Alert>}
      {activeTab === CATALOG_TABS.POSTULATIONS &&
        featuredContextOptionsError && (
          <Alert type="error">{featuredContextOptionsError}</Alert>
        )}
      <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
        <Title title="Catálogo" />
        <span className="subtitle">{pageSubtitle}</span>
      </div>
      <div className="catalog-container__tabs">
        <Tabs
          value={activeTab}
          onChange={(event, nextTab) => setActiveTab(nextTab)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label="Postulaciones al catálogo"
            value={CATALOG_TABS.POSTULATIONS}
          />
          <Tab
            label="Ocasiones del catálogo"
            value={CATALOG_TABS.SETTINGS}
          />
        </Tabs>
      </div>
      {activeTab === CATALOG_TABS.POSTULATIONS ? (
        <TableSite
          data={postulations}
          columns={columns}
          isLoadingData={isLoading}
          loader={isUpdatingStatus}
          style={{ display: 'none' }}
          filter={
            <div
              style={{
                marginTop: '16px',
                marginBottom: '16px',
                display: 'grid',
                gap: '12px',
              }}
            >
            <Grid
              container
              spacing={2}
              style={{
                margin: 0,
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Grid item>
                <Autocomplete
                  options={CATEGORY_OPTIONS}
                  getOptionLabel={(option) => option || ''}
                  value={categoryFilter || null}
                  onChange={(event, newValue) => {
                    setCategoryFilter(newValue || '');
                  }}
                  fullWidth
                  clearOnEscape
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Categoría"
                      placeholder="Buscar categoría"
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  options={Object.entries(STATUS_METADATA).map(
                    ([value, metadata]) => ({
                      value,
                      label: metadata.label,
                    })
                  )}
                  getOptionLabel={(option) => option?.label || ''}
                  getOptionSelected={(option, value) =>
                    Boolean(option) &&
                    Boolean(value) &&
                    option.value === value.value
                  }
                  value={
                    statusFilter
                      ? Object.entries(STATUS_METADATA)
                          .map(([value, metadata]) => ({
                            value,
                            label: metadata.label,
                          }))
                          .find((option) => option.value === statusFilter) || null
                      : null
                  }
                  onChange={(event, newValue) => {
                    setStatusFilter(newValue?.value || '');
                  }}
                  fullWidth
                  clearOnEscape
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Estado"
                      placeholder="Buscar estado"
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  options={featuredContextOptions}
                  getOptionLabel={getCatalogOccasionLabel}
                  getOptionSelected={(option, value) =>
                    Boolean(option) &&
                    Boolean(value) &&
                    option.name === value.name
                  }
                  value={
                    featuredContextOptions.find(
                      (occasion) =>
                        occasion.name === featuredContextFilter
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFeaturedContextFilter(newValue?.name || '');
                  }}
                  fullWidth
                  clearOnEscape
                  autoHighlight
                  disabled={
                    isLoadingFeaturedContextOptions ||
                    Boolean(featuredContextOptionsError)
                  }
                  loading={isLoadingFeaturedContextOptions}
                  loadingText="Cargando ocasiones..."
                  noOptionsText="No hay ocasiones creadas"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Contexto"
                      placeholder="Buscar contexto"
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  options={regionOptions}
                  getOptionLabel={(option) => option || ''}
                  value={regionFilter || null}
                  onChange={(event, newValue) => {
                    setRegionFilter(newValue || '');
                  }}
                  fullWidth
                  clearOnEscape
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Región"
                      placeholder="Buscar región"
                      size="small"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              style={{
                margin: 0,
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Grid item>
                <Search
                  onChange={handleSearchPostulations}
                  style={{ width: '269px' }}
                />
              </Grid>
            </Grid>
            </div>
          }
        />
      ) : (
        <CatalogPageSettings />
      )}
      <Modal
        className="modal"
        open={rejectionModal.isOpen}
        onClose={closeRejectionModal}>
        <ModalContent
          title="Cambiar a Rechazado"
          onClick={closeRejectionModal}
          contentStyle={{ padding: '32px' }}>
          <div
            style={{
              width: '100%',
              minWidth: '480px',
              maxWidth: '560px',
              margin: '0 auto',
            }}>
            <div style={{ display: 'grid', gap: '12px' }}>
              <span className="subtitle">Motivo de rechazo</span>
              <TextField
                fullWidth
                multiline
                rows={5}
                variant="outlined"
                placeholder="Escribe aquí el motivo de rechazo"
                value={rejectionModal.reason}
                onChange={(event) => {
                  const value = event.target.value;

                  setRejectionModal((currentModal) => ({
                    ...currentModal,
                    reason: value,
                    error: '',
                  }));
                }}
              />
              {rejectionModal.error ? (
                <span style={{ color: '#CF1322', fontSize: '14px' }}>
                  {rejectionModal.error}
                </span>
              ) : null}
            </div>
            <div
              className="modal__action-container"
              style={{
                marginTop: '24px',
                width: '100%',
                gridTemplateColumns: '1fr 1fr',
                justifyContent: 'center',
              }}>
              <Button secondary onClick={closeRejectionModal}>
                Cancelar
              </Button>
              <Button onClick={confirmRejectedStatus} loading={isUpdatingStatus}>
                Aceptar
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CatalogContainer;
